import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { adminAPI } from '../services/api';
import CreateConferenceModal from '../components/CreateConferenceModal';
import CreateBlogModal from '../components/CreateBlogModal';
import {
  Calendar,
  FileText,
  DollarSign,
  Clock,
  LogOut,
  Check,
  X,
  Edit,
  Trash2,
  PlusCircle,
  Loader2,
  Settings as SettingsIcon,
  Mail,
  MailOpen,
  Eye,
  Tag,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Download,
  Upload,
  ArrowLeftRight,
  Globe,
  ImageIcon
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [conferences, setConferences] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount: '', maxUses: '', expiryDate: '' });
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [sheetsLoading, setSheetsLoading] = useState(null);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [showScrapeDialog, setShowScrapeDialog] = useState(false);
  const [stats, setStats] = useState({
    totalConferences: 0,
    pendingConferences: 0,
    totalBlogs: 0,
    revenue: 0,
    unreadMessages: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        adminAPI.conferences.getAll(),
        adminAPI.blog.getAll(),
        adminAPI.transactions.getAll(),
        adminAPI.stats.get(),
        adminAPI.messages.getAll(),
        adminAPI.coupons.getAll()
      ]);

      const get = (i) => results[i].status === 'fulfilled' ? results[i].value.data : [];
      const getObj = (i, def) => results[i].status === 'fulfilled' ? results[i].value.data : def;

      // Check for auth errors
      const authFailed = results.some(r => r.status === 'rejected' && r.reason?.response?.status === 401);
      if (authFailed) { navigate('/admin/login'); return; }

      setConferences(get(0));
      setBlogPosts(get(1));
      setTransactions(get(2));
      setStats(getObj(3, { totalConferences: 0, pendingConferences: 0, totalBlogs: 0, revenue: 0, unreadMessages: 0 }));
      setMessages(get(4));
      setCoupons(get(5));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully'
    });
    navigate('/admin/login');
  };

  const handleApprove = async (id) => {
    try {
      await adminAPI.conferences.update(id, { status: 'approved' });
      setConferences(
        conferences.map((conf) =>
          conf.id === id ? { ...conf, status: 'approved' } : conf
        )
      );
      toast({
        title: 'Conference Approved',
        description: 'The conference has been published successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve conference',
        variant: 'destructive'
      });
    }
  };

  const handleReject = async (id) => {
    try {
      await adminAPI.conferences.delete(id);
      setConferences(conferences.filter((conf) => conf.id !== id));
      toast({
        title: 'Conference Rejected',
        description: 'The conference has been removed'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject conference',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === 'conference') {
        await adminAPI.conferences.delete(id);
        setConferences(conferences.filter((conf) => conf.id !== id));
        toast({ title: 'Conference Deleted' });
      } else {
        await adminAPI.blog.delete(id);
        setBlogPosts(blogPosts.filter((post) => post.id !== id));
        toast({ title: 'Blog Post Deleted' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete item',
        variant: 'destructive'
      });
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await adminAPI.messages.markRead(id);
      setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m));
      setStats(s => ({ ...s, unreadMessages: Math.max(0, s.unreadMessages - 1) }));
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to mark message as read', variant: 'destructive' });
    }
  };

  const handleDeleteMessage = async (id) => {
    try {
      const msg = messages.find(m => m.id === id);
      await adminAPI.messages.delete(id);
      setMessages(messages.filter(m => m.id !== id));
      if (msg && !msg.read) setStats(s => ({ ...s, unreadMessages: Math.max(0, s.unreadMessages - 1) }));
      toast({ title: 'Message Deleted', description: 'The message has been deleted' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete message', variant: 'destructive' });
    }
  };

  const handleReply = async (id) => {
    if (!replyText.trim()) {
      toast({ title: 'Error', description: 'Please write a reply', variant: 'destructive' });
      return;
    }
    setReplySending(true);
    try {
      const res = await adminAPI.messages.reply(id, replyText);
      setMessages(messages.map(m => m.id === id ? { ...m, read: true, replies: [...(m.replies || []), { text: replyText, sentAt: new Date().toISOString() }] } : m));
      if (!messages.find(m => m.id === id)?.read) setStats(s => ({ ...s, unreadMessages: Math.max(0, s.unreadMessages - 1) }));
      setReplyingTo(null);
      setReplyText('');
      if (res.data?.email_warning) {
        toast({ title: 'Reply Saved', description: `Reply saved but email failed: ${res.data.email_warning}`, variant: 'destructive' });
      } else {
        toast({ title: 'Reply Sent', description: 'Your reply has been sent from hello@allseoconferences.com' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to send reply', variant: 'destructive' });
    } finally {
      setReplySending(false);
    }
  };


  const handleCreateCoupon = async () => {
    if (!newCoupon.code || !newCoupon.discount) {
      toast({ title: 'Error', description: 'Code and discount are required', variant: 'destructive' });
      return;
    }
    try {
      await adminAPI.coupons.create({
        code: newCoupon.code,
        discount: Number(newCoupon.discount),
        maxUses: newCoupon.maxUses ? Number(newCoupon.maxUses) : null,
        expiryDate: newCoupon.expiryDate || null
      });
      const res = await adminAPI.coupons.getAll();
      setCoupons(res.data);
      setNewCoupon({ code: '', discount: '', maxUses: '', expiryDate: '' });
      setShowCouponForm(false);
      toast({ title: 'Coupon Created', description: `Coupon ${newCoupon.code.toUpperCase()} created` });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to create coupon', variant: 'destructive' });
    }
  };

  const handleToggleCoupon = async (id, active) => {
    try {
      await adminAPI.coupons.update(id, { active: !active });
      setCoupons(coupons.map(c => c.id === id ? { ...c, active: !active } : c));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update coupon', variant: 'destructive' });
    }
  };

  const handleDeleteCoupon = async (id) => {
    try {
      await adminAPI.coupons.delete(id);
      setCoupons(coupons.filter(c => c.id !== id));
      toast({ title: 'Coupon Deleted' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete coupon', variant: 'destructive' });
    }
  };

  const handleSyncStripe = async () => {
    setSyncLoading(true);
    try {
      const res = await adminAPI.transactions.syncStripe();
      const { synced } = res.data;
      toast({ title: 'Stripe Sync Complete', description: `${synced} new transaction(s) imported` });
      const [txnRes, statsRes] = await Promise.all([adminAPI.transactions.getAll(), adminAPI.stats.get()]);
      setTransactions(txnRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast({ title: 'Sync Failed', description: err.response?.data?.detail || 'Failed to sync Stripe payments', variant: 'destructive' });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleToggleExclude = async (txnId, currentExcluded) => {
    const newExcluded = !currentExcluded;
    try {
      await adminAPI.transactions.toggleExclude(txnId, newExcluded);
      setTransactions(prev => prev.map(t => t.id === txnId ? { ...t, excluded: newExcluded } : t));
      const statsRes = await adminAPI.stats.get();
      setStats(statsRes.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update transaction', variant: 'destructive' });
    }
  };

  const handleSheets = async (action) => {
    setSheetsLoading(action);
    try {
      let res;
      if (action === 'scrapeImport') {
        res = await adminAPI.sheets.scrapeImport(scrapeUrl || 'https://collaborator.pro/blog/top-seo-conferences-2026');
        setShowScrapeDialog(false);
      } else if (action === 'repairImages') {
        res = await adminAPI.sheets.repairImages();
      } else {
        res = await adminAPI.sheets[action]();
      }
      toast({ title: 'Success', description: res.data.message });
      if (action === 'import' || action === 'sync' || action === 'scrapeImport' || action === 'repairImages') {
        const confRes = await adminAPI.conferences.getAll();
        setConferences(confRes.data);
        const statsRes = await adminAPI.stats.get();
        setStats(statsRes.data);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || `Failed to ${action}`, variant: 'destructive' });
    } finally {
      setSheetsLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Admin Dashboard
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              allseoconferences.com
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/settings">
              <Button variant="outline">
                <SettingsIcon className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline">View Site</Button>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Conferences
              </h3>
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.totalConferences}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Pending Review
              </h3>
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.pendingConferences}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Blog Posts
              </h3>
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.totalBlogs}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Revenue
              </h3>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              ${stats.revenue.toFixed(2)}
            </p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Unread Messages
              </h3>
              <Mail className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {stats.unreadMessages}
            </p>
          </Card>
        </div>

        {/* Management Tabs */}
        <Tabs defaultValue="conferences" className="space-y-6">
          <TabsList>
            <TabsTrigger value="conferences">Conferences</TabsTrigger>
            <TabsTrigger value="blog">Blog Posts</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="messages" className="relative">
              Messages
              {stats.unreadMessages > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                  {stats.unreadMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="coupons">Coupons</TabsTrigger>
          </TabsList>

          {/* Conferences Tab */}
          <TabsContent value="conferences">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Manage Conferences
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSheets('export')}
                    disabled={!!sheetsLoading}
                    data-testid="sheets-export-btn"
                  >
                    {sheetsLoading === 'export' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    Export
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSheets('import')}
                    disabled={!!sheetsLoading}
                    data-testid="sheets-import-btn"
                  >
                    {sheetsLoading === 'import' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Import
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSheets('sync')}
                    disabled={!!sheetsLoading}
                    data-testid="sheets-sync-btn"
                  >
                    {sheetsLoading === 'sync' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-1" />}
                    Sync
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => { setScrapeUrl('https://collaborator.pro/blog/top-seo-conferences-2026'); setShowScrapeDialog(true); }}
                    disabled={!!sheetsLoading}
                    data-testid="sheets-scrape-import-btn"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Globe className="w-4 h-4 mr-1" />
                    Scrape & Import
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSheets('repairImages')}
                    disabled={!!sheetsLoading}
                    data-testid="repair-images-btn"
                  >
                    {sheetsLoading === 'repairImages' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1" />}
                    Repair Images
                  </Button>
                  <CreateConferenceModal onSuccess={fetchAllData} />
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conferences.map((conf) => (
                      <TableRow key={conf.id}>
                        <TableCell className="font-medium">{conf.title}</TableCell>
                        <TableCell>{new Date(conf.date).toLocaleDateString()}</TableCell>
                        <TableCell>{conf.location}</TableCell>
                        <TableCell>
                          {conf.paymentId && conf.paymentId !== '' ? (
                            conf.paymentId === 'admin_created' ? (
                              <span className="text-xs text-gray-400 italic">admin</span>
                            ) : conf.paymentId.startsWith('migrated') ? (
                              <span className="text-xs text-gray-400 italic">imported</span>
                            ) : (
                              <span className="font-mono text-xs text-green-600" title={conf.paymentId}>{conf.paymentId.substring(0, 16)}...</span>
                            )
                          ) : (
                            <span className="text-xs text-red-400">unpaid</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              conf.status === 'approved' ? 'default' : 'secondary'
                            }
                          >
                            {conf.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {conf.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleApprove(conf.id)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleReject(conf.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <CreateConferenceModal editData={conf} onSuccess={fetchAllData} />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(conf.id, 'conference')}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Manage Blog Posts
                </h2>
                <CreateBlogModal onSuccess={fetchAllData} />
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Published</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blogPosts.map((post) => (
                      <TableRow key={post.id}>
                        <TableCell className="font-medium">{post.title}</TableCell>
                        <TableCell>{post.author}</TableCell>
                        <TableCell>
                          {new Date(post.publishedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CreateBlogModal editData={post} onSuccess={fetchAllData} />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(post.id, 'blog')}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Payment Transactions
                </h2>
                <Button
                  onClick={handleSyncStripe}
                  disabled={syncLoading}
                  variant="outline"
                  data-testid="sync-stripe-btn"
                >
                  {syncLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  {syncLoading ? 'Syncing...' : 'Sync from Stripe'}
                </Button>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Include</TableHead>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn.id} className={txn.excluded ? 'opacity-50' : ''}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={!txn.excluded}
                            onChange={() => handleToggleExclude(txn.id, txn.excluded)}
                            className="w-4 h-4 accent-purple-600 cursor-pointer"
                            data-testid={`txn-include-${txn.id}`}
                            title={txn.excluded ? 'Click to include in revenue' : 'Click to exclude from revenue'}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {txn.paymentId?.substring(0, 20)}...
                        </TableCell>
                        <TableCell className={txn.excluded ? 'line-through text-gray-400' : ''}>
                          ${txn.amount} {txn.currency}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{txn.provider}</Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(txn.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {txn.excluded ? (
                            <Badge className="bg-gray-400">excluded</Badge>
                          ) : (
                            <Badge className={txn.status === 'success' ? 'bg-green-600' : 'bg-yellow-600'}>
                              {txn.status}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Contact Messages
                  {stats.unreadMessages > 0 && (
                    <span className="text-sm font-medium text-red-500">({stats.unreadMessages} unread)</span>
                  )}
                </h2>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {messages.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">No messages yet</div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      data-testid={`message-${msg.id}`}
                      className={`p-6 ${!msg.read ? 'bg-purple-50 dark:bg-purple-900/10' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            {!msg.read ? (
                              <Mail className="w-4 h-4 text-purple-600 flex-shrink-0" />
                            ) : (
                              <MailOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className={`font-semibold ${!msg.read ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                              {msg.name}
                            </span>
                            <span className="text-sm text-gray-500">&lt;{msg.email}&gt;</span>
                            {!msg.read && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-full">New</span>
                            )}
                          </div>
                          {msg.subject && (
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 ml-7">
                              {msg.subject}
                            </p>
                          )}
                          <p className="text-sm text-gray-600 dark:text-gray-400 ml-7 whitespace-pre-wrap">
                            {msg.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2 ml-7">
                            {new Date(msg.createdAt).toLocaleString()}
                          </p>
                          {/* Previous replies */}
                          {msg.replies && msg.replies.length > 0 && (
                            <div className="ml-7 mt-3 space-y-2">
                              {msg.replies.map((r, i) => (
                                <div key={i} className={r.direction === 'inbound'
                                  ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3'
                                  : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3'
                                }>
                                  <p className={`text-xs font-medium mb-1 ${r.direction === 'inbound' ? 'text-blue-700 dark:text-blue-400' : 'text-green-700 dark:text-green-400'}`}>
                                    {r.direction === 'inbound'
                                      ? `Received from ${r.from || msg.email}`
                                      : 'Replied from hello@allseoconferences.com'
                                    } &bull; {new Date(r.sentAt).toLocaleString()}
                                  </p>
                                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{r.text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Reply form */}
                          {replyingTo === msg.id && (
                            <div className="ml-7 mt-3 border border-purple-200 dark:border-purple-800 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/10">
                              <p className="text-xs text-gray-500 mb-2">Replying to <strong>{msg.name}</strong> &lt;{msg.email}&gt; from <strong>hello@allseoconferences.com</strong></p>
                              <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write your reply..."
                                rows={4}
                                data-testid={`reply-text-${msg.id}`}
                                className="mb-3"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleReply(msg.id)}
                                  disabled={replySending}
                                  data-testid={`send-reply-${msg.id}`}
                                  className="bg-purple-600 hover:bg-purple-700 text-white"
                                >
                                  {replySending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                                  {replySending ? 'Sending...' : 'Send Reply'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!msg.read && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkRead(msg.id)}
                              data-testid={`mark-read-${msg.id}`}
                              title="Mark as read"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setReplyingTo(replyingTo === msg.id ? null : msg.id); setReplyText(''); }}
                            data-testid={`reply-${msg.id}`}
                            title="Reply"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteMessage(msg.id)}
                            data-testid={`delete-msg-${msg.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons">
            <Card>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Tag className="w-5 h-5" /> Coupon Codes
                </h2>
                <Button onClick={() => setShowCouponForm(!showCouponForm)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-coupon-btn">
                  <PlusCircle className="w-4 h-4 mr-2" /> Create Coupon
                </Button>
              </div>

              {showCouponForm && (
                <div className="p-6 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Code *</label>
                      <input
                        value={newCoupon.code}
                        onChange={(e) => setNewCoupon(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                        placeholder="e.g., SAVE20"
                        className="w-full px-3 py-2 border rounded-md text-sm uppercase dark:bg-gray-900 dark:border-gray-700"
                        data-testid="coupon-code-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Discount % *</label>
                      <input
                        type="number"
                        value={newCoupon.discount}
                        onChange={(e) => setNewCoupon(p => ({ ...p, discount: e.target.value }))}
                        placeholder="e.g., 20"
                        min="1" max="100"
                        className="w-full px-3 py-2 border rounded-md text-sm dark:bg-gray-900 dark:border-gray-700"
                        data-testid="coupon-discount-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Max Uses</label>
                      <input
                        type="number"
                        value={newCoupon.maxUses}
                        onChange={(e) => setNewCoupon(p => ({ ...p, maxUses: e.target.value }))}
                        placeholder="Unlimited"
                        className="w-full px-3 py-2 border rounded-md text-sm dark:bg-gray-900 dark:border-gray-700"
                        data-testid="coupon-maxuses-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Expiry Date</label>
                      <input
                        type="date"
                        value={newCoupon.expiryDate}
                        onChange={(e) => setNewCoupon(p => ({ ...p, expiryDate: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md text-sm dark:bg-gray-900 dark:border-gray-700"
                        data-testid="coupon-expiry-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleCreateCoupon} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-coupon-btn">
                      <Check className="w-4 h-4 mr-2" /> Save Coupon
                    </Button>
                    <Button variant="outline" onClick={() => { setShowCouponForm(false); setNewCoupon({ code: '', discount: '', maxUses: '', expiryDate: '' }); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Code</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Discount</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Price After</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Used</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Max Uses</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Expiry</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {coupons.length === 0 ? (
                      <tr><td colSpan="8" className="p-8 text-center text-gray-500">No coupons yet</td></tr>
                    ) : (
                      coupons.map((coupon) => (
                        <tr key={coupon.id} data-testid={`coupon-${coupon.id}`}>
                          <td className="p-4 font-mono font-bold text-purple-600 dark:text-purple-400">{coupon.code}</td>
                          <td className="p-4">{coupon.discount}%</td>
                          <td className="p-4 font-semibold text-green-600">${(49 * (1 - coupon.discount / 100)).toFixed(2)}</td>
                          <td className="p-4">{coupon.usageCount || 0}</td>
                          <td className="p-4">{coupon.maxUses || 'Unlimited'}</td>
                          <td className="p-4 text-sm">{coupon.expiryDate ? new Date(coupon.expiryDate).toLocaleDateString() : 'Never'}</td>
                          <td className="p-4">
                            <button onClick={() => handleToggleCoupon(coupon.id, coupon.active)} data-testid={`toggle-coupon-${coupon.id}`}>
                              {coupon.active ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded-full">
                                  <ToggleRight className="w-3 h-3" /> Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500 rounded-full">
                                  <ToggleLeft className="w-3 h-3" /> Inactive
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="p-4">
                            <Button size="sm" variant="destructive" onClick={() => handleDeleteCoupon(coupon.id)} data-testid={`delete-coupon-${coupon.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showScrapeDialog} onOpenChange={setShowScrapeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Scrape & Import Conferences</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enter blog post URL to scrape conferences from:
            </label>
            <input
              type="url"
              value={scrapeUrl}
              onChange={(e) => setScrapeUrl(e.target.value)}
              placeholder="https://example.com/blog/conferences"
              className="w-full px-3 py-2 border rounded-md text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              data-testid="scrape-url-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScrapeDialog(false)}>Cancel</Button>
            <Button
              onClick={() => handleSheets('scrapeImport')}
              disabled={!scrapeUrl || sheetsLoading === 'scrapeImport'}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="scrape-confirm-btn"
            >
              {sheetsLoading === 'scrapeImport' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Globe className="w-4 h-4 mr-1" />}
              Scrape
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
